import { useState } from 'react'
import { M365User } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { ResetPasswordModal } from './ResetPasswordModal'

interface UserDetailHeaderProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onUserUpdated: (user: M365User) => void
}

export function UserDetailHeader({
  user,
  customerId,
  customerTenantId,
  onUserUpdated,
}: UserDetailHeaderProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)
  const [showForcePasswordChangeConfirm, setShowForcePasswordChangeConfirm] = useState(false)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleToggleStatus = async () => {
    try {
      setActionLoading('status')
      setActionError(null)
      const token = await getCustomerGraphAccessToken(customerTenantId)
      await api.setM365UserStatus(token, customerId, user.id, !user.accountEnabled)

      onUserUpdated({ ...user, accountEnabled: !user.accountEnabled })
      setActionSuccess(`User sign-in ${user.accountEnabled ? 'blocked' : 'unblocked'} successfully`)
      setTimeout(() => setActionSuccess(null), 3000)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update user status')
    } finally {
      setActionLoading(null)
    }
  }

  const handleSignOutAllSessions = async () => {
    try {
      setActionLoading('signout')
      setActionError(null)
      setShowSignOutConfirm(false)
      const token = await getCustomerGraphAccessToken(customerTenantId)
      await api.revokeM365UserSessions(token, customerId, user.id)

      setActionSuccess('User has been signed out of all sessions')
      setTimeout(() => setActionSuccess(null), 3000)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to sign out user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleForcePasswordChange = async () => {
    try {
      setActionLoading('forcePasswordChange')
      setActionError(null)
      setShowForcePasswordChangeConfirm(false)
      const token = await getCustomerGraphAccessToken(customerTenantId)
      await api.forceM365PasswordChange(token, customerId, user.id)

      setActionSuccess('User will be required to change password at next sign-in')
      setTimeout(() => setActionSuccess(null), 3000)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to set force password change')
    } finally {
      setActionLoading(null)
    }
  }

  const getInitials = (name?: string) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }

  return (
    <>
      {/* Success message */}
      {actionSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <svg className="h-5 w-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm text-green-700">{actionSuccess}</span>
        </div>
      )}

      {/* Error message */}
      {actionError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-red-700">{actionError}</span>
          </div>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Avatar */}
              <div className={`h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold ${
                user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'
              }`}>
                {getInitials(user.displayName)}
              </div>

              {/* User info */}
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-gray-900">{user.displayName}</h2>
                <p className="text-sm text-gray-500">{user.userPrincipalName}</p>
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.accountEnabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <span className={`h-2 w-2 rounded-full mr-1.5 ${
                      user.accountEnabled ? 'bg-green-400' : 'bg-red-400'
                    }`}></span>
                    {user.accountEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  {user.jobTitle && (
                    <span className="ml-3 text-sm text-gray-500">{user.jobTitle}</span>
                  )}
                  {user.department && (
                    <span className="ml-2 text-sm text-gray-400">| {user.department}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowResetPasswordModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Reset Password
              </button>
              <button
                onClick={() => setShowForcePasswordChangeConfirm(true)}
                disabled={actionLoading !== null}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {actionLoading === 'forcePasswordChange' ? (
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : (
                  <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Force Password Change
              </button>
              <button
                onClick={() => setShowSignOutConfirm(true)}
                disabled={actionLoading !== null}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {actionLoading === 'signout' ? (
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : (
                  <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                )}
                Sign out
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={actionLoading !== null}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                  user.accountEnabled
                    ? 'border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500'
                    : 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
                }`}
              >
                {actionLoading === 'status' ? (
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : user.accountEnabled ? (
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {user.accountEnabled ? 'Block sign-in' : 'Unblock sign-in'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <ResetPasswordModal
          user={user}
          customerId={customerId}
          customerTenantId={customerTenantId}
          onClose={() => setShowResetPasswordModal(false)}
        />
      )}

      {/* Sign Out Confirmation Modal */}
      {showSignOutConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowSignOutConfirm(false)}></div>

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:align-middle">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Sign out of all sessions</h3>
                <button type="button" onClick={() => setShowSignOutConfirm(false)} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-4">
                <div className="flex items-center mb-4">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                    {getInitials(user.displayName)}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                    <p className="text-sm text-gray-500">{user.userPrincipalName}</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Are you sure?</h4>
                      <p className="mt-1 text-sm text-yellow-700">
                        This will sign the user out of all Microsoft 365 sessions on all devices. They will need to sign in again.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowSignOutConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSignOutAllSessions}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Sign out user
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Password Change Confirmation Modal */}
      {showForcePasswordChangeConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowForcePasswordChangeConfirm(false)}></div>

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:align-middle">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Force Password Change</h3>
                <button type="button" onClick={() => setShowForcePasswordChangeConfirm(false)} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-4">
                <div className="flex items-center mb-4">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                    {getInitials(user.displayName)}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                    <p className="text-sm text-gray-500">{user.userPrincipalName}</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">What will happen?</h4>
                      <p className="mt-1 text-sm text-blue-700">
                        The user will be required to change their password the next time they sign in. Their current password will continue to work until they sign in.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForcePasswordChangeConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleForcePasswordChange}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Require Password Change
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
